/**
 * Main Application Controller
 * Artisan Roaster Pro - Coffee Roasting Monitor & Control
 */

import BluetoothManager from './bluetooth.js';
import { SerialManager } from './serial.js';
import ChartingManager from './charting.js';
import StorageManager from './storage.js';
import NotificationsManager from './notifications.js';

class RoasterApp {
    constructor() {
        // Initialize managers
        this.bluetooth = new BluetoothManager();
        this.serial = new SerialManager();
        this.charts = new ChartingManager();
        this.storage = new StorageManager();
        this.notifications = new NotificationsManager();

        // Connection mode: 'bluetooth' or 'serial'
        this.connectionMode = null;

        // App state
        this.currentView = 'dashboard';
        this.isRecording = false;
        this.sessionData = null;
        this.timer = null;
        this.elapsedTime = 0;
        this.lastBT = null;
        this.lastET = null;
        this.lastRoR = null;
        this.fcTime = null; // First Crack time for DTR calculation
        this.sessionMetadata = {
            beanName: '',
            origin: '',
            initialWeight: 0,
            finalWeight: 0,
            notes: ''
        };

        // UI Elements
        this.elements = {};

        // Settings
        this.settings = {};
    }

    /**
     * Check browser compatibility
     */
    checkBrowserCompatibility() {
        if (!navigator.bluetooth) {
            // Detect browser
            const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
            const isSafari = navigator.userAgent.toLowerCase().indexOf('safari') > -1 &&
                navigator.userAgent.toLowerCase().indexOf('chrome') === -1;

            let browserName = 'your current browser';
            if (isFirefox) browserName = 'Firefox';
            if (isSafari) browserName = 'Safari';

            // Show prominent warning
            setTimeout(() => {
                const warning = {
                    type: 'warning',
                    title: '⚠️ Browser Not Supported',
                    message: `${browserName} doesn't support Web Bluetooth. Please use Chrome or Edge for full functionality.`,
                    icon: '🌐'
                };

                // Show persistent notification
                this.notifications.showAlert(warning);

                // Also log to console with instructions
                console.warn('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
                console.warn('⚠️  WEB BLUETOOTH NOT SUPPORTED');
                console.warn('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
                console.warn('');
                console.warn('To use Bluetooth features:');
                console.warn('1. Open this URL in Google Chrome or Microsoft Edge');
                console.warn('2. URL: ' + window.location.href);
                console.warn('');
                console.warn('Supported browsers:');
                console.warn('✅ Chrome (Desktop & Android)');
                console.warn('✅ Edge (Desktop)');
                console.warn('❌ Firefox - Not supported');
                console.warn('❌ Safari - Limited support');
                console.warn('');
                console.warn('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
            }, 1000);
        } else {
            console.log('✅ Web Bluetooth API supported');
        }
    }

    /**
     * Initialize application
     */
    async init() {
        console.log('🚀 Initializing JCL Artisan Roaster...');

        // Check browser compatibility
        this.checkBrowserCompatibility();

        // Initialize storage
        await this.storage.init();
        this.settings = this.storage.getSettings();

        // Initialize notifications
        await this.notifications.init();
        this.notifications.updateSettings(this.settings);

        // Cache DOM elements
        this.cacheElements();

        // Setup event listeners
        this.setupEventListeners();

        // Initialize charts
        this.charts.initTempChart('tempChart');
        this.charts.initRoRChart('rorChart');

        // Setup Bluetooth and Serial
        this.setupBluetooth();
        this.setupSerial();

        // Load initial data
        await this.loadProfiles();
        await this.loadHistory();

        // Apply settings
        this.applySettings();

        console.log('✅ Application initialized');
    }

    /**
     * Cache DOM elements
     */
    cacheElements() {
        this.elements = {
            // Connection
            btnUSB: document.getElementById('btnUSB'),
            btnBluetooth: document.getElementById('btnBluetooth'),
            connectionStatus: document.getElementById('connectionStatus'),
            bluetoothIcon: document.getElementById('bluetoothIcon'),
            usbIcon: document.getElementById('usbIcon'),

            // Temperature displays
            btValue: document.getElementById('btValue'),
            etValue: document.getElementById('etValue'),
            rorValue: document.getElementById('rorValue'),
            timerValue: document.getElementById('timerValue'),
            dtrValue: document.getElementById('dtrValue'),

            // Session controls
            btnStartSession: document.getElementById('btnStartSession'),
            btnStopSession: document.getElementById('btnStopSession'),
            roastEventGroup: document.getElementById('roastEventGroup'),
            btnEventCharge: document.getElementById('btnEventCharge'),
            btnEventTP: document.getElementById('btnEventTP'),
            btnEventDryEnd: document.getElementById('btnEventDryEnd'),
            btnEventFC: document.getElementById('btnEventFC'),
            btnEventDrop: document.getElementById('btnEventDrop'),
            btnLoadGhost: document.getElementById('btnLoadGhost'),

            // Views
            views: document.querySelectorAll('.view'),
            navButtons: document.querySelectorAll('.nav-btn'),

            // Modals
            modalBluetooth: document.getElementById('modalBluetooth'),
            modalMilestone: document.getElementById('modalMilestone'),
            modalPreRoast: document.getElementById('modalPreRoast'),
            modalPostRoast: document.getElementById('modalPostRoast'),
            btnScanDevices: document.getElementById('btnScanDevices'),
            btnSaveMilestone: document.getElementById('btnSaveMilestone'),
            btnConfirmPreRoast: document.getElementById('btnConfirmPreRoast'),
            btnConfirmPostRoast: document.getElementById('btnConfirmPostRoast'),
            
            // Pre/Post Roast Fields
            preBeanName: document.getElementById('preBeanName'),
            preOrigin: document.getElementById('preOrigin'),
            preInitialWeight: document.getElementById('preInitialWeight'),
            postFinalWeight: document.getElementById('postFinalWeight'),
            postWeightLoss: document.getElementById('postWeightLoss'),
            postNotes: document.getElementById('postNotes'),

            // Lists
            profilesList: document.getElementById('profilesList'),
            historyList: document.getElementById('historyList'),
            
            // Profiles View
            btnNewProfile: document.getElementById('btnNewProfile'),
            btnSaveProfile: document.getElementById('btnSaveProfile'),
            newProfileName: document.getElementById('newProfileName'),
            newProfileDesc: document.getElementById('newProfileDesc'),

            // Settings
            settingEnableAlerts: document.getElementById('settingEnableAlerts'),
            settingTempHigh: document.getElementById('settingTempHigh'),
            settingEnableSound: document.getElementById('settingEnableSound'),
            settingAutoSave: document.getElementById('settingAutoSave'),
            settingSampleRate: document.getElementById('settingSampleRate'),
            settingBaudRate: document.getElementById('settingBaudRate'),
            settingShowDebug: document.getElementById('settingShowDebug'),
            debugLog: document.getElementById('debugLog'),
            btnExportData: document.getElementById('btnExportData'),
            btnImportData: document.getElementById('btnImportData'),

            // Filters
            filterDate: document.getElementById('filterDate'),
            filterRoastLevel: document.getElementById('filterRoastLevel')
        };
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // USB Serial button
        this.elements.btnUSB?.addEventListener('click', async () => {
            await this.connectSerial();
        });

        // Bluetooth button
        this.elements.btnBluetooth?.addEventListener('click', () => {
            this.showModal('modalBluetooth');
        });

        this.elements.btnScanDevices?.addEventListener('click', async () => {
            await this.connectBluetooth();
        });

        // Session controls
        this.elements.btnStartSession?.addEventListener('click', () => {
            const isConnected = this.bluetooth.isConnected || this.serial.isConnected;
            if (!isConnected) {
                this.notifications.showAlert({
                    type: 'warning', title: 'Not Connected', message: 'Please connect via USB or Bluetooth first', icon: '⚠️'
                });
                return;
            }
            // Show Pre-Roast Modal instead of starting immediately
            this.showModal('modalPreRoast');
        });
        
        this.elements.btnConfirmPreRoast?.addEventListener('click', () => {
            // Collect metadata
            this.sessionMetadata.beanName = this.elements.preBeanName.value || 'Unknown Bean';
            this.sessionMetadata.origin = this.elements.preOrigin.value || '';
            this.sessionMetadata.initialWeight = parseFloat(this.elements.preInitialWeight.value) || 0;
            
            this.hideModal('modalPreRoast');
            this.startSession();
        });

        this.elements.btnStopSession?.addEventListener('click', () => {
            // Stopping manually without Drop event
            this.stopRecordingAndPrompt();
        });

        // Specific Event Markers
        this.elements.btnEventCharge?.addEventListener('click', () => {
            this.charts.addMilestone('Charge', '#3b82f6');
            this.notifications.showAlert({ type: 'info', title: 'Event', message: 'Charge marked', icon: '📥' });
        });
        
        this.elements.btnEventTP?.addEventListener('click', () => {
            this.charts.addMilestone('Turning Point', '#a855f7');
            this.notifications.showAlert({ type: 'info', title: 'Event', message: 'Turning Point marked', icon: '↩️' });
        });

        this.elements.btnEventDryEnd?.addEventListener('click', () => {
            this.charts.addMilestone('Dry End', '#fbbf24');
            this.notifications.showAlert({ type: 'info', title: 'Event', message: 'Dry End marked', icon: '🟡' });
        });

        this.elements.btnEventFC?.addEventListener('click', () => {
            this.charts.addMilestone('First Crack', '#ef4444');
            this.fcTime = this.elapsedTime; // Start DTR calculation
            this.notifications.showAlert({ type: 'warning', title: 'First Crack', message: 'First Crack marked. DTR started.', icon: '💥' });
        });

        this.elements.btnEventDrop?.addEventListener('click', () => {
            this.charts.addMilestone('Drop', '#10b981');
            this.notifications.showAlert({ type: 'success', title: 'Drop', message: 'Roast finished.', icon: '📤' });
            this.stopRecordingAndPrompt();
        });
        
        // Post Roast Finalization
        this.elements.postFinalWeight?.addEventListener('input', (e) => {
            const finalWeight = parseFloat(e.target.value) || 0;
            const initialWeight = this.sessionMetadata.initialWeight;
            if(initialWeight > 0 && finalWeight > 0) {
                const loss = ((initialWeight - finalWeight) / initialWeight) * 100;
                this.elements.postWeightLoss.textContent = loss.toFixed(1) + '%';
                this.elements.postWeightLoss.style.color = loss > 20 ? '#ef4444' : '#10b981';
            } else {
                this.elements.postWeightLoss.textContent = '--%';
            }
        });
        
        this.elements.btnConfirmPostRoast?.addEventListener('click', async () => {
            this.settings.sampleRate = parseInt(this.elements.settingSampleRate.value) || 10;
            if (this.elements.settingBaudRate) {
                this.settings.baudRate = parseInt(this.elements.settingBaudRate.value) || 115200;
            }
            this.sessionMetadata.finalWeight = parseFloat(this.elements.postFinalWeight.value) || 0;
            this.sessionMetadata.notes = this.elements.postNotes.value || '';
            
            this.hideModal('modalPostRoast');
            
            // Save session if auto-save enabled (or manually trigger save)
            if (this.settings.autoSave) {
                await this.saveSession();
            } else {
                // If not auto-saving, we might still want to give them an option to save explicitly,
                // but for now let's just save it since they clicked "Save Session" on the modal
                await this.saveSession();
            }
            
            // Clear fields for next time
            this.elements.preBeanName.value = '';
            this.elements.preOrigin.value = '';
            this.elements.preInitialWeight.value = '';
            this.elements.postFinalWeight.value = '';
            this.elements.postNotes.value = '';
            this.elements.postWeightLoss.textContent = '--%';
        });

        // Load Ghost Reference
        this.elements.btnLoadGhost?.addEventListener('click', () => {
            this.loadGhostProfile();
        });

        // Navigation
        this.elements.navButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                const view = e.currentTarget.dataset.view;
                this.switchView(view);
            });
        });
        
        // Profiles View
        this.elements.btnNewProfile?.addEventListener('click', () => {
            this.elements.newProfileName.value = '';
            this.elements.newProfileDesc.value = '';
            this.showModal('modalNewProfile');
        });
        
        this.elements.btnSaveProfile?.addEventListener('click', async () => {
            const name = this.elements.newProfileName.value.trim();
            const desc = this.elements.newProfileDesc.value.trim();
            
            if(!name) {
                this.notifications.showAlert({ type: 'warning', title: 'Required Field', message: 'Profile name is required.', icon: '⚠️' });
                return;
            }
            
            await this.storage.saveProfile({ name: name, description: desc });
            this.hideModal('modalNewProfile');
            this.notifications.showAlert({ type: 'success', title: 'Success', message: 'Profile created!', icon: '✅' });
            await this.loadProfiles();
        });

        // Modal close buttons
        document.querySelectorAll('.modal-close').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const modal = e.target.closest('.modal');
                modal?.classList.remove('active');
            });
        });

        // Click outside modal to close
        document.querySelectorAll('.modal').forEach(modal => {
            modal.addEventListener('click', (e) => {
                if (e.target === modal) {
                    modal.classList.remove('active');
                }
            });
        });

        // Settings
        this.elements.settingEnableAlerts?.addEventListener('change', (e) => {
            this.updateSetting('enableAlerts', e.target.checked);
        });

        this.elements.settingTempHigh?.addEventListener('change', (e) => {
            this.updateSetting('tempHigh', parseFloat(e.target.value));
        });

        this.elements.settingEnableSound?.addEventListener('change', (e) => {
            this.updateSetting('enableSound', e.target.checked);
        });

        this.elements.settingAutoSave?.addEventListener('change', (e) => {
            this.updateSetting('autoSave', e.target.checked);
        });

        this.elements.settingSampleRate?.addEventListener('change', (e) => {
            this.updateSetting('sampleRate', parseInt(e.target.value));
        });

        // Toggle debug log
        this.elements.settingShowDebug?.addEventListener('change', (e) => {
            if (this.elements.debugLog) {
                this.elements.debugLog.style.display = e.target.checked ? 'block' : 'none';
            }
        });

        // Data management
        this.elements.btnExportData?.addEventListener('click', () => {
            this.exportData();
        });

        this.elements.btnImportData?.addEventListener('click', () => {
            this.importData();
        });

        // History filters
        this.elements.filterDate?.addEventListener('change', () => {
            this.loadHistory();
        });

        this.elements.filterRoastLevel?.addEventListener('change', () => {
            this.loadHistory();
        });
    }

    /**
     * Setup Bluetooth manager
     */
    setupBluetooth() {
        // Handle incoming data
        this.bluetooth.onData((data) => {
            this.handleBluetoothData(data);
        });

        // Handle status changes
        this.bluetooth.onStatusChange((status) => {
            this.updateConnectionStatus(status);
        });
    }

    /**
     * Connect to Bluetooth device
     */
    async connectBluetooth() {
        try {
            await this.bluetooth.connect();
            this.connectionMode = 'bluetooth';
            this.hideModal('modalBluetooth');
            this.notifications.showAlert({
                type: 'success',
                title: 'Connected',
                message: `Connected to ${this.bluetooth.getDeviceInfo()?.name}`,
                icon: '✅'
            });
        } catch (error) {
            console.error('Connection failed:', error);
            this.notifications.showAlert({
                type: 'error',
                title: 'Connection Failed',
                message: error.message,
                icon: '❌'
            });
        }
    }

    /**
     * Setup Serial manager
     */
    setupSerial() {
        // Handle incoming data
        this.serial.onData((rawData) => {
            this.handleSerialData(rawData);
        });

        // Handle status changes  
        this.serial.onStatusChange((status) => {
            this.updateConnectionStatus(status, 'serial');
        });
    }

    /**
     * Connect to USB Serial device
     */
    async connectSerial() {
        try {
            if (!this.serial.isSupported()) {
                throw new Error('Web Serial API not supported. Please use Chrome or Edge.');
            }

            const baudRate = this.settings.baudRate || 115200;
            await this.serial.connect(baudRate);
            this.connectionMode = 'serial';

            this.notifications.showAlert({
                type: 'success',
                title: 'USB Connected',
                message: 'Connected via USB Serial',
                icon: '🔌'
            });
        } catch (error) {
            console.error('Serial connection failed:', error);
            this.notifications.showAlert({
                type: 'error',
                title: 'USB Connection Failed',
                message: error.message,
                icon: '❌'
            });
        }
    }

    /**
     * Handle incoming Serial data
     */
    handleSerialData(rawData) {
        // Log to debug panel if enabled
        if (this.elements.settingShowDebug?.checked && this.elements.debugLog) {
            this.elements.debugLog.innerHTML += `<div>${new Date().toISOString().split('T')[1].slice(0,8)} - ${rawData}</div>`;
            this.elements.debugLog.scrollTop = this.elements.debugLog.scrollHeight;
        }
        
        // Parse using same parser as Bluetooth
        const data = this.parseTemperatureData(rawData);
        if (data) {
            this.handleBluetoothData(data); // Reuse same handler
        }
    }

    /**
     * Parse temperature data from serial string
     */
    parseTemperatureData(data) {
        // Pattern 1: "BT:230.5,ET:195.2,STATUS:READY"
        const pattern1 = /BT:([\d.]+),ET:([\d.]+)/i;
        const match1 = data.match(pattern1);
        if (match1) {
            return {
                bt: parseFloat(match1[1]),
                et: parseFloat(match1[2]),
                timestamp: Date.now()
            };
        }

        // Pattern 2: Simple CSV "230.5,195.2"
        const pattern2 = /([\d.]+),([\d.]+)/;
        const match2 = data.match(pattern2);
        if (match2) {
            return {
                bt: parseFloat(match2[1]),
                et: parseFloat(match2[2]),
                timestamp: Date.now()
            };
        }

        return null;
    }

    /**
     * Handle incoming Bluetooth data
     */
    handleBluetoothData(data) {
        if (!data || data.bt === null || data.et === null) return;

        // Update displays
        this.updateTemperatureDisplays(data.bt, data.et);

        // If recording, add to chart
        if (this.isRecording) {
            const chartData = this.charts.addDataPoint(data.bt, data.et, data.timestamp);

            // Update RoR display
            if (chartData.ror !== null) {
                this.elements.rorValue.textContent = chartData.ror.toFixed(1);
                this.lastRoR = chartData.ror;

                // Check thresholds
                this.notifications.checkThresholds(data.bt, data.et, chartData.ror);
            }

            // Store for session
            this.lastBT = data.bt;
            this.lastET = data.et;
        }
    }

    /**
     * Update temperature displays
     */
    updateTemperatureDisplays(bt, et) {
        this.elements.btValue.textContent = bt.toFixed(1);
        this.elements.etValue.textContent = et.toFixed(1);
    }

    /**
     * Update connection status
     */
    updateConnectionStatus(status, mode = null) {
        const connectionType = mode || this.connectionMode;

        const statusMap = {
            disconnected: { text: 'Disconnected', class: 'disconnected', btIcon: '📡', usbIcon: '🔌' },
            scanning: { text: 'Scanning...', class: 'connecting', btIcon: '🔍', usbIcon: '🔍' },
            connecting: { text: 'Connecting...', class: 'connecting', btIcon: '⏳', usbIcon: '⏳' },
            connected: { text: 'Connected', class: 'connected', btIcon: '✅', usbIcon: '✅' },
            error: { text: 'Error', class: 'disconnected', btIcon: '❌', usbIcon: '❌' }
        };

        const state = statusMap[status] || statusMap.disconnected;

        this.elements.connectionStatus.textContent = state.text;
        this.elements.connectionStatus.className = `status-badge ${state.class}`;

        // Update appropriate icon
        if (connectionType === 'serial') {
            this.elements.usbIcon.textContent = state.usbIcon;
        } else {
            this.elements.bluetoothIcon.textContent = state.btIcon;
        }
    }

    /**
     * Start roast session
     */
    startSession() {
        const isConnected = this.bluetooth.isConnected || this.serial.isConnected;

        if (!isConnected) {
            this.notifications.showAlert({
                type: 'warning',
                title: 'Not Connected',
                message: 'Please connect via USB or Bluetooth first',
                icon: '⚠️'
            });
            return;
        }

        this.isRecording = true;
        this.charts.reset();
        this.elapsedTime = 0;
        this.fcTime = null;
        if(this.elements.dtrValue) this.elements.dtrValue.textContent = '--%';

        // Start timer
        this.timer = setInterval(() => {
            this.elapsedTime++;
            this.updateTimer();
        }, 1000);

        // Update UI
        this.elements.btnStartSession.style.display = 'none';
        this.elements.btnStopSession.style.display = 'flex';
        this.elements.roastEventGroup.style.display = 'flex';

        console.log('▶️ Session started');

        this.notifications.showAlert({
            type: 'success',
            title: 'Session Started',
            message: 'Recording roast session',
            icon: '▶️'
        });
    }

    /**
     * Stop recording and show post-roast prompt
     */
    stopRecordingAndPrompt() {
        if (!this.isRecording) return;
        
        this.isRecording = false;

        // Stop timer
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = null;
        }

        // Update UI
        this.elements.btnStartSession.style.display = 'flex';
        this.elements.btnStopSession.style.display = 'none';
        this.elements.roastEventGroup.style.display = 'none';

        console.log('⏹️ Recording stopped. Waiting for summary.');

        // Show Post-Roast Modal
        this.showModal('modalPostRoast');
    }

    /**
     * Update timer display
     */
    updateTimer() {
        const minutes = Math.floor(this.elapsedTime / 60);
        const seconds = this.elapsedTime % 60;
        this.elements.timerValue.textContent =
            `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
            
        // Calculate DTR if FC is reached
        if (this.fcTime !== null && this.elapsedTime > 0) {
            const devTime = this.elapsedTime - this.fcTime;
            const dtr = (devTime / this.elapsedTime) * 100;
            if(this.elements.dtrValue) {
                this.elements.dtrValue.textContent = dtr.toFixed(1) + '%';
            }
        }
    }

    /**
     * Save current session
     */
    async saveSession() {
        const chartData = this.charts.getData();

        const session = {
            startTime: Date.now() - (this.elapsedTime * 1000),
            endTime: Date.now(),
            duration: this.elapsedTime,
            dataPoints: chartData,
            peakBT: chartData.bt.length > 0 ? Math.max(...chartData.bt.map(d => d.y)) : 0,
            peakET: chartData.et.length > 0 ? Math.max(...chartData.et.map(d => d.y)) : 0,
            deviceName: this.bluetooth.getDeviceInfo()?.name || 'Unknown',
            metadata: this.sessionMetadata
        };

        try {
            const id = await this.storage.saveSession(session);
            console.log('Session saved:', id);

            this.notifications.showAlert({
                type: 'success',
                title: 'Session Saved',
                message: 'Roast session saved successfully',
                icon: '💾'
            });

            await this.loadHistory();

        } catch (error) {
            console.error('Failed to save session:', error);
            this.notifications.showAlert({
                type: 'error',
                title: 'Save Failed',
                message: error.message,
                icon: '❌'
            });
        }
    }

    /**
     * Save milestone
     */
    saveMilestone() {
        const type = document.getElementById('milestoneType').value;
        const notes = document.getElementById('milestoneNotes').value;

        const milestoneLabels = {
            'first-crack': 'First Crack',
            'second-crack': 'Second Crack',
            'development': 'Development',
            'custom': notes || 'Custom'
        };

        const label = milestoneLabels[type];
        this.charts.addMilestone(label);

        const minutes = Math.floor(this.elapsedTime / 60);
        const seconds = this.elapsedTime % 60;
        const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

        this.notifications.notifyMilestone(type, {
            bt: this.lastBT,
            et: this.lastET,
            time: timeStr
        });

        this.hideModal('modalMilestone');
        document.getElementById('milestoneNotes').value = '';
    }

    /**
     * Load Ghost Profile
     */
    async loadGhostProfile() {
        const sessions = await this.storage.getAllSessions();
        if (sessions.length === 0) {
            this.notifications.showAlert({
                type: 'warning', title: 'No History', message: 'No past sessions available to load.', icon: '📉'
            });
            return;
        }
        
        // For simplicity, we just load the most recent session as ghost profile
        const latestSession = sessions[0];
        if(latestSession && latestSession.dataPoints) {
            this.charts.loadGhostData(latestSession.dataPoints);
            this.notifications.showAlert({
                type: 'success', title: 'Reference Loaded', message: 'Loaded last roast as reference curve.', icon: '👻'
            });
        }
    }

    /**
     * Load profiles
     */
    async loadProfiles() {
        const profiles = await this.storage.getAllProfiles();

        if (profiles.length === 0) {
            this.elements.profilesList.innerHTML = `
                <div class="empty-state">
                    <div class="empty-icon">📈</div>
                    <p>No roast profiles yet</p>
                    <p class="text-muted">Create your first profile to get started</p>
                </div>
            `;
            return;
        }

        // Render profiles
        this.elements.profilesList.innerHTML = profiles.map(profile => `
            <div class="profile-card" style="display: flex; justify-content: space-between; align-items: flex-start;">
                <div>
                    <h3>${profile.name}</h3>
                    <p class="text-muted">${profile.description || 'No description'}</p>
                </div>
                <button class="btn-icon btn-delete-profile" data-id="${profile.id}" title="Delete Profile" style="color: #ef4444; background: none; border: none; cursor: pointer; font-size: 1.2rem; padding: 0.5rem;">🗑️</button>
            </div>
        `).join('');

        // Bind delete buttons
        this.elements.profilesList.querySelectorAll('.btn-delete-profile').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const id = parseInt(e.currentTarget.dataset.id);
                if(confirm('Are you sure you want to delete this profile?')) {
                    await this.storage.deleteProfile(id);
                    this.notifications.showAlert({ type: 'success', title: 'Deleted', message: 'Profile deleted.', icon: '🗑️' });
                    this.loadProfiles();
                }
            });
        });
    }

    /**
     * Load history
     */
    async loadHistory() {
        const filters = {
            date: this.elements.filterDate?.value || null,
            roastLevel: this.elements.filterRoastLevel?.value || null
        };

        const sessions = await this.storage.getAllSessions(filters);

        if (sessions.length === 0) {
            this.elements.historyList.innerHTML = `
                <div class="empty-state">
                    <div class="empty-icon">📚</div>
                    <p>No roast sessions yet</p>
                    <p class="text-muted">Your roast history will appear here</p>
                </div>
            `;
            return;
        }

        // Render history
        this.elements.historyList.innerHTML = sessions.map(session => {
            const date = new Date(session.timestamp).toLocaleDateString();
            const time = new Date(session.timestamp).toLocaleTimeString();
            const duration = `${Math.floor(session.duration / 60)}:${(session.duration % 60).toString().padStart(2, '0')}`;
            const meta = session.metadata || {};
            const beanName = meta.beanName || session.deviceName || 'Roast Session';
            
            let weightInfo = '';
            if (meta.initialWeight > 0 && meta.finalWeight > 0) {
                const loss = ((meta.initialWeight - meta.finalWeight) / meta.initialWeight * 100).toFixed(1);
                weightInfo = `<span>⚖️ Loss: ${loss}%</span>`;
            }

            return `
                <div class="history-item" style="display: flex; justify-content: space-between; align-items: center;">
                    <div class="history-info" style="flex-grow: 1;">
                        <h3>${beanName} ${meta.origin ? `<small style="color: #b0b0b0;">(${meta.origin})</small>` : ''}</h3>
                        <div class="history-details" style="gap: 1rem; flex-wrap: wrap;">
                            <span>📅 ${date} ${time}</span>
                            <span>⏱️ ${duration}</span>
                            <span>🌡️ Peak: ${session.peakBT?.toFixed(1)}°C</span>
                            ${weightInfo}
                        </div>
                        ${meta.notes ? `<p class="text-muted" style="margin-top: 0.5rem; font-size: 0.9rem;">${meta.notes}</p>` : ''}
                    </div>
                    <button class="btn-icon btn-delete-history" data-id="${session.id}" title="Delete History" style="color: #ef4444; background: none; border: none; cursor: pointer; font-size: 1.2rem; padding: 0.5rem; margin-left: 1rem;">🗑️</button>
                </div>
            `;
        }).join('');

        // Bind delete buttons
        this.elements.historyList.querySelectorAll('.btn-delete-history').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const id = parseInt(e.currentTarget.dataset.id);
                if(confirm('Are you sure you want to delete this roast session?')) {
                    await this.storage.deleteSession(id);
                    this.notifications.showAlert({ type: 'success', title: 'Deleted', message: 'Session deleted.', icon: '🗑️' });
                    this.loadHistory();
                }
            });
        });
    }

    /**
     * Switch view
     */
    switchView(viewName) {
        this.currentView = viewName;

        // Update nav buttons
        this.elements.navButtons.forEach(btn => {
            btn.classList.toggle('active', btn.dataset.view === viewName);
        });

        // Update views
        this.elements.views.forEach(view => {
            view.classList.toggle('active', view.id === `view${viewName.charAt(0).toUpperCase() + viewName.slice(1)}`);
        });
    }

    /**
     * Show modal
     */
    showModal(modalId) {
        const modal = document.getElementById(modalId);
        modal?.classList.add('active');
    }

    /**
     * Hide modal
     */
    hideModal(modalId) {
        const modal = document.getElementById(modalId);
        modal?.classList.remove('active');
    }

    /**
     * Update setting
     */
    updateSetting(key, value) {
        this.settings[key] = value;
        this.storage.saveSettings(this.settings);
        this.notifications.updateSettings(this.settings);
        console.log('Setting updated:', key, value);
    }

    /**
     * Apply settings to UI
     */
    applySettings() {
        if (this.elements.settingEnableAlerts) {
            this.elements.settingEnableAlerts.checked = this.settings.enableAlerts;
        }
        if (this.elements.settingTempHigh) {
            this.elements.settingTempHigh.value = this.settings.tempHigh;
        }
        if (this.elements.settingEnableSound) {
            this.elements.settingEnableSound.checked = this.settings.enableSound;
        }
        if (this.elements.settingAutoSave) {
            this.elements.settingAutoSave.checked = this.settings.autoSave;
        }
        if (this.elements.settingSampleRate) {
            this.elements.settingSampleRate.value = this.settings.sampleRate || 10;
        }
        if (this.elements.settingBaudRate) {
            this.elements.settingBaudRate.value = this.settings.baudRate || 115200;
        }
    }

    /**
     * Export all data
     */
    async exportData() {
        const data = await this.storage.exportAllData();
        const json = JSON.stringify(data, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `roaster-export-${Date.now()}.json`;
        a.click();

        URL.revokeObjectURL(url);

        this.notifications.showAlert({
            type: 'success',
            title: 'Data Exported',
            message: 'All data exported successfully',
            icon: '💾'
        });
    }

    /**
     * Import data
     */
    importData() {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'application/json';

        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            try {
                const text = await file.text();
                const data = JSON.parse(text);
                await this.storage.importData(data);

                await this.loadProfiles();
                await this.loadHistory();
                this.applySettings();

                this.notifications.showAlert({
                    type: 'success',
                    title: 'Data Imported',
                    message: 'All data imported successfully',
                    icon: '📥'
                });
            } catch (error) {
                console.error('Import failed:', error);
                this.notifications.showAlert({
                    type: 'error',
                    title: 'Import Failed',
                    message: error.message,
                    icon: '❌'
                });
            }
        };

        input.click();
    }
}

// Initialize app when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
    const app = new RoasterApp();
    app.init();

    // Make app accessible globally for debugging
    window.roasterApp = app;
});
